import type { TExample } from '@atlas/uicorn/preview/modules';
import { useContext, useImperativeHandle, useRef, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import useRefCallback from 'use-ref-callback';
import { Input as AntInput, type InputProps as TAntInputProps } from 'antd';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';
import Popover, { type TPopoverRef } from '@atlas/uicorn/lib/Display/Popover';
import DatePicker, { type TDatePickerProps } from '@atlas/uicorn/lib/Control/DatePicker';
import combineEventHandlers from '@atlas/tool/box/event/combineEventHandlers';
import hotkey from '@atlas/tool/box/event/hotkey';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import CalendarIcon from '@atlas/uicorn/lib/Icon/CalendarIcon';
import type { TSpecificPrimitive } from '@atlas/tool/types/TSpecificPrimitive';
import type { TTimezoneIdentifier } from '@atlas/tool/box/date/timezones';

const DateInput = React.memo(
    React.forwardRef<TDateInputRef, TDateInputProps>(function DateInput(props, forwardedRef): React.ReactElement {
        const { disabled: contextDisabled } = useContext(FormContext);

        const {
            onChange,
            value: propsValue,
            defaultValue: propsDefaultValue,
            disabled: propsDisabled,
            showTime,
            standardFormat,
            disabledDate,
            timezone,
            ...inputProps
        } = props;

        const disabled = propsDisabled ?? contextDisabled;

        const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
        const popoverRef = useRef<TPopoverRef>(null);

        const [value, setValue] = useControlledState<string | undefined>(
            propsValue,
            (value) => {
                if (value === undefined) return undefined;
                onChange?.(value as any);
            },
            propsDefaultValue ?? '',
        );

        const handleChange = useRefCallback((newValue: string) => {
            setValue(standardFormat ? moment(newValue).format(showTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD') : newValue);
            if (!showTime) popoverRef.current?.close();
        });

        useImperativeHandle(forwardedRef, () => inputEl as any, [inputEl]);

        return (
            <SDateInputPopover
                content={({ opened }) => {
                    if (!opened) return null;

                    const container = popoverRef.current?.getDialog();
                    if (!container) return null;

                    return (
                        <DatePicker
                            value={value ? moment(value) : undefined}
                            showTime={showTime ? { format: 'HH:mm' } : undefined}
                            disabledDate={disabledDate}
                            timezone={timezone}
                            onChange={handleChange}
                        />
                    );
                }}
                ref={popoverRef}
            >
                {({ ref, open, close }) => (
                    <SInput
                        {...inputProps}
                        value={value}
                        disabled={disabled}
                        suffix={props.suffix ?? <SCalendarIcon />}
                        onClick={combineEventHandlers(inputProps.onClick, open)}
                        onFocus={combineEventHandlers(inputProps.onFocus, open)}
                        onBlur={combineEventHandlers(inputProps.onBlur, close)}
                        onKeyDown={combineEventHandlers(inputProps.onKeyDown, (e) => {
                            if (escapeHotkey.matches(e)) {
                                e.preventDefault();
                                close();
                            } else if (backspaceHotkey.matches(e)) {
                                e.preventDefault();
                                setValue(undefined);
                                open();
                            }
                        })}
                        ref={(r) => {
                            ref(r?.input || null);
                            setInputEl(r?.input || null);
                        }}
                        autoComplete="off"
                    />
                )}
            </SDateInputPopover>
        );
    }),
);

export default DateInput;

// #region Helpers

const backspaceHotkey = hotkey('backspace', 'hidden');

const escapeHotkey = hotkey('escape', 'hidden');

// #endregion

// #region Styles

const SCalendarIcon = styled(CalendarIcon)`
    color: ${(p) => p.theme.color.grey7};
`;

export const SDateInputPopover = styled(Popover)`
    width: fit-content;
    min-width: auto;
`;

const SInput = styled(AntInput)`
    ${(p) => p.theme.patch.input}

    .ant-input {
        background: transparent;
        border-radius: unset;
    }
`;

// #endregion

// #region Types

export type TDateString = TSpecificPrimitive<string, 'yyyy-mm-dd'>;
export type TDateTimeString = TSpecificPrimitive<string, 'yyyy-mm-dd hh:ii'>;

export type TDateInputProps = Pick<
    TAntInputProps,
    'className' | 'name' | 'placeholder' | 'required' | 'disabled' | 'autoFocus' | 'prefix' | 'suffix'
> & {
    state?: 'default' | 'warn' | 'error';
    disabledDate?: TDatePickerProps['disabledDate'];
    timezone?: TTimezoneIdentifier;
    onFocus?: TAntInputProps['onFocus'];
    onBlur?: TAntInputProps['onBlur'];
    onInput?: TAntInputProps['onInput'];
    onClick?: TAntInputProps['onClick'];
    onKeyDown?: TAntInputProps['onKeyDown'];
    onPressEnter?: TAntInputProps['onPressEnter'];
} & ( // TODO: Should be standardized by moving all to the backend format YYYY-MM-DD
        | {
              standardFormat?: false;
              value?: string;
              defaultValue?: string;
              onChange?: (newValue?: string) => void;
              showTime?: boolean;
          }
        | {
              standardFormat: true;
              value?: TDateString;
              defaultValue?: TDateString;
              onChange?: (newValue?: TDateString) => void;
              showTime?: false;
          }
        | {
              standardFormat: true;
              value?: TDateTimeString;
              defaultValue?: TDateTimeString;
              onChange?: (newValue?: TDateTimeString) => void;
              showTime: true;
          }
    );

export type TDateInputRef = HTMLInputElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <DateInput
                value={props.value}
                placeholder={props.placeholder}
                disabled={props.disabled}
                showTime={props.showTime}
                prefix={props.prefix}
                suffix={props.suffix}
                onChange={props.onChange}
                standardFormat={props.standardFormat}
            />
        );
    },
    controls: {
        state: ['default', 'warn', 'error'],
        value: { type: ['2023-01-01', '2024-01-01'], connected: false },
        placeholder: 'string',
        disabled: 'boolean',
        showTime: 'boolean',
        prefix: { type: 'string', connected: false },
        suffix: { type: 'string', connected: false },
        standardFormat: 'boolean',
    },
    handlers: {
        onChange: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('DateInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
